import { useState, useEffect } from "react";
import api from "../../services/api";

const TYPE_ICON = { entry: "📥", exit: "📤", adjustment: "🔧", inventory: "📋", return: "↩️", transfer: "🔄" };
const TYPE_LABEL = { entry: "Entrée", exit: "Sortie", adjustment: "Ajustement", inventory: "Inventaire", return: "Retour", transfer: "Transfert" };
const TYPE_COLOR = { entry: "#1a9e72", exit: "#e24b4a", adjustment: "#ba7517", inventory: "#185fa5", return: "#8b5cf6", transfer: "#0891b2" };

function fmt(n) { return (n || 0).toLocaleString("fr-FR", { maximumFractionDigits: 0 }); }
function fmtDec(n) { return (n || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }

export default function DashboardTab({ currentCompanyId, onNewProduct }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!currentCompanyId) return;
    setLoading(true);
    api.stocks.dashboard(currentCompanyId)
      .then(setData)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [currentCompanyId]);

  if (loading) return <div style={{ textAlign: "center", padding: 60, color: "var(--text-muted)" }}>Chargement…</div>;
  if (!data) return null;

  const stockPct = data.totalProducts > 0 ? Math.round((1 - data.outOfStock / data.totalProducts) * 100) : 100;

  return (
    <div>
       <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16 }}>
  <button className="btn primary" onClick={onNewProduct}>
    + Nouveau produit
  </button>
</div>

      {/* KPIs */}
      <div className="kpi-grid k4" style={{ marginBottom: 20 }}>
        <div className="kpi">
          <div className="kpi-label">Produits actifs</div>
          <div className="kpi-value">{data.totalProducts}</div>
          <div className="kpi-sub" style={{ color: "var(--text-muted)" }}>{data.outOfStock} en rupture</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Valeur FIFO stock</div>
          <div className="kpi-value" style={{ color: "var(--g)" }}>{fmtDec(data.totalValue)} €</div>
          <div className="kpi-sub up">Coût d'acquisition</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Alertes stock bas</div>
          <div className="kpi-value" style={{ color: data.alerts > 0 ? "var(--amber)" : "var(--g)" }}>{data.alerts}</div>
          <div className="kpi-sub" style={{ color: data.alerts > 0 ? "var(--amber)" : "var(--text-muted)" }}>
            {data.alerts > 0 ? "Réapprovisionnement requis" : "Tous les niveaux OK"}
          </div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Disponibilité</div>
          <div className="kpi-value" style={{ color: stockPct >= 90 ? "var(--g)" : "var(--amber)" }}>{stockPct}%</div>
          <div className="kpi-sub">{data.totalProducts - data.outOfStock} / {data.totalProducts} en stock</div>
        </div>
      </div>

      <div className="g2">
        {/* Alertes */}
        {data.alertList?.length > 0 && (
          <div className="card">
            <div style={{ fontWeight: 700, marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}>
              <span>⚠️</span> Alertes de réapprovisionnement
              <span style={{ marginLeft: "auto", fontSize: 11, background: "#ba751722", color: "#ba7517", padding: "2px 8px", borderRadius: 8, fontWeight: 700 }}>{data.alertList.length}</span>
            </div>
            {data.alertList.map(p => {
              const pct = p.stock_min > 0 ? Math.round((p.stock_current / p.stock_min) * 100) : 0;
              return (
                <div key={p.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: "var(--border)" }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{p.name}</div>
                    <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{p.sku} · {p.category}</div>
                  </div>
                  <div style={{ textAlign: "right", minWidth: 80 }}>
                    <div style={{ fontWeight: 700, color: p.stock_current <= 0 ? "var(--red)" : "var(--amber)" }}>
                      {p.stock_current} {p.unit}
                    </div>
                    <div style={{ fontSize: 11, color: "var(--text-muted)" }}>min {p.stock_min}</div>
                  </div>
                  <div style={{ width: 50, height: 6, background: "#f1f5f9", borderRadius: 4, overflow: "hidden" }}>
                    <div style={{ width: `${Math.min(pct, 100)}%`, height: "100%", background: pct === 0 ? "var(--red)" : "var(--amber)", borderRadius: 4 }} />
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Derniers mouvements */}
        <div className="card">
          <div style={{ fontWeight: 700, marginBottom: 12 }}>Derniers mouvements</div>
          {data.lastMovements?.length === 0 ? (
            <div style={{ color: "var(--text-muted)", fontSize: 13 }}>Aucun mouvement</div>
          ) : (
            data.lastMovements?.map(m => (
              <div key={m.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "7px 0", borderBottom: "var(--border)" }}>
                <span style={{ fontSize: 18, width: 28 }}>{TYPE_ICON[m.type] || "📦"}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{m.product_name}</div>
                  <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{m.reason || TYPE_LABEL[m.type]}</div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontWeight: 700, color: TYPE_COLOR[m.type], fontSize: 13 }}>
                    {m.type === 'exit' ? '−' : '+'}{Math.abs(m.quantity)} {m.unit}
                  </div>
                  <div style={{ fontSize: 10, color: "var(--text-muted)" }}>
                    {new Date(m.created_at).toLocaleDateString("fr-FR")}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Top 10 */}
      {data.top10?.length > 0 && (
        <div className="card" style={{ marginTop: 20 }}>
          <div style={{ fontWeight: 700, marginBottom: 12 }}>Top 10 — Valeur FIFO la plus élevée</div>
          <div className="tbl-overflow">
            <table className="tbl">
              <thead>
                <tr><th>#</th><th>Produit</th><th>Catégorie</th><th>SKU</th><th>Stock</th><th>Valeur FIFO</th></tr>
              </thead>
              <tbody>
                {data.top10.map((p, i) => (
                  <tr key={p.id}>
                    <td style={{ fontWeight: 700, color: i < 3 ? "var(--amber)" : "var(--text-muted)" }}>#{i + 1}</td>
                    <td style={{ fontWeight: 600 }}>{p.name}</td>
                    <td><span className="badge info">{p.category}</span></td>
                    <td style={{ fontFamily: "monospace", color: "var(--text-muted)", fontSize: 12 }}>{p.sku}</td>
                    <td>{p.stock_current} {p.unit}</td>
                    <td style={{ fontWeight: 700, color: "var(--g)" }}>{fmtDec(p.fifo_value)} €</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
